import type { EventThreadRow } from '../api/conversations';
import type { EventGroup, ThreadListRow } from '../ui/ThreadList';
import { formatDayInZone } from './datetime';

/*
 * How the events section is arranged, in one place because two screens arrange it.
 *
 * A member reaches these through `/units/` and a parent through `/family/`, and rule 13b is that
 * the second mirrors the first in routes and never in rules. A second copy of "which month does
 * this belong to" is a rule, and it would drift the first time somebody changed how a month is
 * decided.
 */

/** One month of events, with the unit's own kinds nested inside it. */
export interface EventMonthGroup {
  /** `yyyy-MM`, exactly as the server filed it — in the event's zone, not the reader's. */
  month: string;
  /** "August 2026". */
  label: string;
  groups: EventGroup[];
}

/**
 * `2026-08` to "August 2026".
 *
 * Built at noon UTC rather than midnight: a date constructed at midnight and formatted in a
 * negative-offset zone lands on the last day of the previous month, which would put a heading a
 * month out for every reader west of Greenwich.
 */
export function monthLabel(month: string): string {
  const [year, index] = month.split('-').map(Number);

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, index - 1, 1, 12)));
}

/**
 * One event thread as a list row.
 *
 * The date is rendered in the **event's** zone, which is not always the unit's and is very rarely
 * the reader's — a Denver campout reads as Denver's Friday wherever it is being read.
 */
export function toListRow(row: EventThreadRow): ThreadListRow {
  return {
    id: row.thread.id,
    title: row.thread.title,
    preview: row.thread.preview,
    lastMessageAt: row.thread.lastMessageAt,
    unreadCount: row.thread.unreadCount,
    meta: `${formatDayInZone(row.startsAt, row.timeZoneId)}${row.cancelled ? ' · Cancelled' : ''}`,
  };
}

/**
 * The window's rows as months, each grouped by the unit's own name for the kind of event.
 *
 * Month before kind, because *when* is the coarse question and *what sort* is the fine one.
 * Grouping by kind first put a campout from last spring in the same block as next weekend's, which
 * is what made this section unreadable against real history.
 *
 * The rows arrive already ordered by month and then by start time, so nothing here re-sorts them —
 * the server decided the window and the order, and re-deciding either would be a second answer.
 */
export function monthGroupsOf(rows: EventThreadRow[]): EventMonthGroup[] {
  const months = new Map<string, EventMonthGroup>();

  for (const row of rows) {
    let month = months.get(row.month);

    if (!month) {
      month = { month: row.month, label: monthLabel(row.month), groups: [] };
      months.set(row.month, month);
    }

    const kind = row.eventTypeName ?? null;
    const existing = month.groups.find((g) => g.kind === kind);
    const listRow = toListRow(row);

    if (existing) {
      existing.rows.push(listRow);
    } else {
      month.groups.push({
        kind,
        colorKey: row.eventTypeColorKey,
        rows: [listRow],
      });
    }
  }

  return [...months.values()];
}

/**
 * Where backwards and forwards go from here.
 *
 * Both step over months that **actually hold events this reader may see** — the server's index is
 * already narrowed by the policy — so neither control can land somebody on an empty page, and
 * neither advertises a month whose events are all in threads they cannot read.
 *
 * Forward skips the whole window rather than one month, because the window may be several months
 * long and stepping into the middle of what is already on screen would look like nothing happened.
 */
export function neighbours(
  index: { month: string; count: number }[],
  // Only `month` is read, and typing it that way is what lets both calendars share this: the
  // conversations page passes EventMonthGroups and the unit's passes its own month groups
  // (TROOP-123). Naming the wider type here would have forced one of them to fake the other's shape.
  shown: { month: string }[],
): { previous: string | null; next: string | null } {
  if (shown.length === 0) {
    return { previous: null, next: null };
  }

  const first = shown[0].month;
  const last = shown[shown.length - 1].month;

  const before = index.filter((m) => m.month < first);
  const after = index.find((m) => m.month > last);

  return {
    previous: before.length > 0 ? before[before.length - 1].month : null,
    next: after?.month ?? null,
  };
}
